import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { setTokens, getUserDataFromToken, saveUserData, type UserData } from '../../services/auth';
import { setSession, signOut } from '../../services/auth';

const AUTH_URL = process.env.NEXT_PUBLIC_BASE_URL || '';

export interface LoginResult {
  success: boolean;
  user?: UserData;
  error?: string;
  errorCode?: string;
}

export interface RegisterResult {
  success: boolean;
  error?: string;
  errorCode?: string;
}

export interface ResendVerificationResult {
  success: boolean;
  message?: string;
  error?: string;
  errorCode?: string;
  retryAfterSeconds?: number;
}

interface UseAuthActionsProps {
  setUser: (user: UserData | null) => void;
  setLoading: (loading: boolean) => void;
  clearAuthData: () => void;
  disconnectWallet?: () => Promise<{ success: boolean; error?: string }>;
}

export function useAuthActions({ setUser, setLoading, clearAuthData, disconnectWallet }: UseAuthActionsProps) {
  const router = useRouter();

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      setLoading(true);
      try {
        const response = await fetch(`${AUTH_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          return handleLoginError(response);
        }

        const data = await response.json();
        const session = data?.data?.session || data?.session || data;

        if (session?.access_token) {
          const { access_token, refresh_token } = session;
          setTokens(access_token, refresh_token);
          await setSession(access_token, refresh_token);

          let userData = getUserDataFromToken(access_token);

          if (userData && data?.data?.user?.created_at) {
            userData.joiningDate = data.data.user.created_at;
            userData.lastSignInAt = data.data.user.last_sign_in_at;
            userData.emailConfirmedAt = data.data.user.email_confirmed_at;

            const apiProfile = data.data.user.user_metadata?.profile || {};
            if (Object.keys(apiProfile).length > 0) {
              userData.profile = {
                country: apiProfile.country || '',
                city: apiProfile.city || '',
                zipCode: apiProfile.zipCode || '',
                bio: apiProfile.bio || '',
                profileImage: apiProfile.profileImage || '',
                coverImage: apiProfile.coverImage || '',
              };
            }
          }

          if (userData) saveUserData(userData);
          setUser(userData);

          return { success: true, user: userData || undefined };
        }

        return { success: false, error: 'Login succeeded but failed to retrieve session data.', errorCode: 'MISSING_SESSION_DATA' };
      } catch (error) {
        console.error('Login fetch/network error:', error);
        clearAuthData();
        return { success: false, error: 'Login failed. Please check your network connection.', errorCode: 'NETWORK_OR_UNEXPECTED' };
      } finally {
        setLoading(false);
      }
    },
    [setUser, setLoading, clearAuthData]
  );

  const logout = useCallback(async () => {
    const { getAccessToken } = await import('../../services/auth');
    const token = getAccessToken();

    if (token) {
      try {
        await fetch(`${AUTH_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error calling logout endpoint:', error);
      }
    }

    // Disconnect wallet on logout (Wagmi)
    if (disconnectWallet) {
      try {
        await disconnectWallet();
      } catch (error) {
        console.error('Error disconnecting wallet:', error);
      }
    }

    // Clear legacy NFTMarketplaceContext wallet storage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('impactoverse_wallet_provider');
    }

    await signOut();
    clearAuthData();
    router.push('/sign-in');
  }, [clearAuthData, router, disconnectWallet]);

  const register = useCallback(
    async (email: string, password: string, metadata?: Record<string, unknown>): Promise<RegisterResult> => {
      setLoading(true);
      try {
        const payload = {
          email,
          password,
          metadata,
          emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/email-confirmed` : undefined,
        };

        const response = await fetch(`${AUTH_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          return handleRegisterError(response);
        }

        return { success: true };
      } catch (error) {
        console.error('Registration fetch/network error:', error);
        return { success: false, error: 'Registration failed. Please check your network connection.', errorCode: 'NETWORK_OR_UNEXPECTED' };
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const resendVerificationEmail = useCallback(
    async (email: string): Promise<ResendVerificationResult> => {
      try {
        const response = await fetch(`${AUTH_URL}/api/auth/resend-verification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/email-confirmed` : undefined,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          return {
            success: false,
            error:
              data.message ||
              data.error ||
              "We couldn't send the verification email. Please try again in a moment.",
            errorCode: data.code || 'UNKNOWN_ERROR',
            retryAfterSeconds: data.retryAfterSeconds,
          };
        }

        return { success: true, message: data.message };
      } catch (error) {
        console.error('Resend verification fetch/network error:', error);
        return {
          success: false,
          error: "We couldn't reach the server. Please check your connection and try again.",
          errorCode: 'NETWORK_OR_UNEXPECTED',
        };
      }
    },
    []
  );

  return { login, logout, register, resendVerificationEmail };
}

async function handleLoginError(response: Response): Promise<LoginResult> {
  let errorMessage = 'Login failed. Please try again.';
  let errorCode = 'UNKNOWN_ERROR';

  try {
    const errorData = await response.json();
    const apiMessage = errorData.error || errorData.message;
    const apiCode = errorData.code;

    if (response.status === 403 && apiCode === 'USER_BLOCKED') {
      errorMessage = 'Your account has been blocked. Please contact support.';
      errorCode = 'USER_BLOCKED';
    } else if (apiCode === 'EMAIL_NOT_CONFIRMED') {
      // The password was correct; the account just never confirmed its email.
      errorMessage = 'Your email address has not been verified yet.';
      errorCode = 'EMAIL_NOT_CONFIRMED';
    } else if (response.status === 401 || response.status === 400) {
      errorMessage = 'Invalid email or password.';
      errorCode = 'INVALID_CREDENTIALS';
    } else if (response.status >= 500) {
      errorMessage = 'Login failed due to a server issue. Please try again later.';
      errorCode = 'SERVER_ERROR';
    } else if (apiMessage) {
      errorMessage = apiMessage;
      errorCode = 'API_CLIENT_ERROR';
    }
  } catch {
    if (response.status === 401 || response.status === 400) {
      errorMessage = 'Invalid email or password.';
      errorCode = 'INVALID_CREDENTIALS';
    } else if (response.status >= 500) {
      errorMessage = 'Login failed due to a server issue. Please try again later.';
      errorCode = 'SERVER_ERROR';
    }
  }

  return { success: false, error: errorMessage, errorCode };
}

async function handleRegisterError(response: Response): Promise<RegisterResult> {
  let errorMessage = 'Registration failed. Please try again.';
  let errorCode = 'UNKNOWN_ERROR';

  try {
    const errorData = await response.json();
    const apiMessage = errorData.error || errorData.message;

    if (errorData.code === 'EMAIL_UNVERIFIED') {
      // The address is taken by an account that never confirmed its email. The
      // signup attempt itself made Supabase re-send the link, so the user is
      // offered a resend rather than being turned away as a duplicate.
      errorMessage = apiMessage || 'This email is registered but not verified yet.';
      errorCode = 'EMAIL_UNVERIFIED';
    } else if (errorData.code === 'EMAIL_SEND_RATE_LIMITED') {
      errorMessage = apiMessage || 'Too many verification emails requested. Please try again shortly.';
      errorCode = 'EMAIL_SEND_RATE_LIMITED';
    } else if (errorData.code === 'VERIFICATION_EMAIL_NOT_SENT') {
      // Checked before the status branches: this arrives as a 502, which would
      // otherwise be swallowed by the generic "server issue" message below and
      // told the user their registration failed when what failed was the email.
      errorMessage =
        apiMessage || "We couldn't send the verification email. Please request a new link.";
      errorCode = 'VERIFICATION_EMAIL_NOT_SENT';
    } else if (response.status === 409) {
      // The API returns 409 for both duplicate emails and username conflicts —
      // keep the API's wording for username conflicts so callers can tell them apart
      if (apiMessage && /username/i.test(apiMessage)) {
        errorMessage = apiMessage;
        errorCode = 'USERNAME_TAKEN';
      } else {
        errorMessage = 'An account with this email already exists.';
        errorCode = 'USER_ALREADY_EXISTS';
      }
    } else if (apiMessage && /already registered/i.test(apiMessage)) {
      // Duplicate signups can also surface as non-409 responses carrying
      // Supabase's exact "User already registered" wording (the backend
      // re-throws it as a 500 when email confirmation is disabled).
      // Deliberately narrow — matching a looser "already exists" could
      // misread an unrelated 5xx and tell a NEW user they have an account.
      errorMessage = 'An account with this email already exists.';
      errorCode = 'USER_ALREADY_EXISTS';
    } else if (response.status >= 400 && response.status < 500) {
      errorMessage = apiMessage || 'Registration failed. Please check your input.';
      errorCode = 'API_CLIENT_ERROR';
    } else if (response.status >= 500) {
      errorMessage = 'Registration failed due to a server issue. Please try again later.';
      errorCode = 'SERVER_ERROR';
    }
  } catch {
    if (response.status === 409) {
      errorMessage = 'An account with this email already exists.';
      errorCode = 'USER_ALREADY_EXISTS';
    }
  }

  return { success: false, error: errorMessage, errorCode };
}
